/**
 * useAuth Hook - Firebase Authentication State Management
 *
 * Provides authentication state, user information, and admin status
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { onAuthStateChanged, signOut as firebaseSignOut, User as FirebaseUser } from 'firebase/auth';
import { auth } from '../services/firebase';
import { getUserDocument, UserStatus } from '../services/userService';
import { User } from '../types';

// Admin emails - can be configured via environment variable
const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || 'pecka@dwx.cz').split(',').map(e => e.trim().toLowerCase());

interface UseAuthReturn {
  user: User | null;
  loading: boolean;
  error: Error | null;
  isAdmin: boolean;
  isApproved: boolean;
  isEmailVerified: boolean;
  userStatus: UserStatus | null;
  signOut: () => Promise<void>;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [userStatus, setUserStatus] = useState<UserStatus | null>(null);
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (firebaseUser: FirebaseUser | null) => {
        if (firebaseUser) {
          // User is signed in
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
          });

          // Check email verification status
          setIsEmailVerified(firebaseUser.emailVerified);

          // Check user approval status from Firestore
          // Admins are always approved - no need to check Firestore
          const isAdminUser = firebaseUser.email && ADMIN_EMAILS.includes(firebaseUser.email.toLowerCase());
          if (isAdminUser) {
            setUserStatus('approved');
          } else {
            try {
              const userDoc = await getUserDocument(firebaseUser.uid);
              if (userDoc) {
                setUserStatus(userDoc.status);
              } else {
                // If no document exists (legacy user), consider approved for now
                // This allows existing users to work without migration
                setUserStatus('approved');
              }
            } catch (err) {
              console.error('Error fetching user status:', err);
              // On Firestore error, allow access for existing users
              // This prevents lockout when Firestore is unavailable
              setUserStatus('approved');
            }
          }
        } else {
          // User is signed out
          setUser(null);
          setUserStatus(null);
          setIsEmailVerified(false);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Auth state change error:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Check if user is admin
  const isAdmin = useMemo(() => {
    if (!user?.email) return false;
    const isAdminUser = ADMIN_EMAILS.includes(user.email.toLowerCase());
    console.log('Admin check:', { email: user.email, adminEmails: ADMIN_EMAILS, isAdmin: isAdminUser });
    return isAdminUser;
  }, [user?.email]);

  // Check if user is approved (admins are always approved)
  const isApproved = useMemo(() => {
    if (isAdmin) return true;
    return userStatus === 'approved';
  }, [isAdmin, userStatus]);

  // Sign out function
  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut(auth);
    } catch (err) {
      console.error('Sign out error:', err);
      throw err;
    }
  }, []);

  return { user, loading, error, isAdmin, isApproved, isEmailVerified, userStatus, signOut };
}
